import React, { useState, useEffect, useRef } from 'react';
import {
  getOnlineOrders,
  getOnlineOrdersCount,
  updateOrderStatus,
  markOrderAsRead,
  getOrderHistory
} from '../../services/api';
import { getWsUrl } from '../../config';
import {
  ShoppingBag,
  Package,
  Truck,
  MapPin,
  User,
  Phone,
  Clock,
  Check,
  Printer,
  ChevronRight,
  AlertCircle,
  X,
  Bell,
  Store,
  Send,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

// Status configurations
const DELIVERY_TYPE_CONFIG = {
  pickup: { label: 'Recoger en tienda', icon: Store, color: 'blue' },
  delivery: { label: 'A domicilio', icon: Truck, color: 'orange' },
  shipping: { label: 'Paquetería', icon: Send, color: 'purple' }
};

const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'red', bg: 'bg-red-100 text-red-700' },
  pending: { label: 'Pendiente', color: 'yellow', bg: 'bg-yellow-100 text-yellow-700' },
  preparing: { label: 'En preparación', color: 'blue', bg: 'bg-blue-100 text-blue-700' },
  ready: { label: 'Listo', color: 'green', bg: 'bg-green-100 text-green-700' },
  ready_for_pickup: { label: 'Listo para recolección', color: 'green', bg: 'bg-green-100 text-green-700' },
  waiting_driver: { label: 'Esperando chofer', color: 'orange', bg: 'bg-orange-100 text-orange-700' },
  waiting_guide: { label: 'Esperando guía', color: 'purple', bg: 'bg-purple-100 text-purple-700' },
  guide_generated: { label: 'Guía generada', color: 'purple', bg: 'bg-purple-200 text-purple-800' },
  in_transit: { label: 'En camino', color: 'blue', bg: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Entregado', color: 'green', bg: 'bg-green-200 text-green-800' },
  shipped: { label: 'Enviado', color: 'green', bg: 'bg-green-200 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'gray', bg: 'bg-gray-100 text-gray-700' }
};

// Get next available statuses based on delivery type and current status
const getNextStatuses = (deliveryType, currentStatus) => {
  const statusMap = {
    pickup: {
      new: ['preparing'],
      pending: ['preparing'],
      preparing: ['ready_for_pickup'],
      ready_for_pickup: ['delivered'],
      ready: ['ready_for_pickup']
    },
    delivery: {
      new: ['preparing'],
      pending: ['preparing'],
      preparing: ['ready', 'waiting_driver'],
      ready: ['waiting_driver'],
      waiting_driver: [] // App del repartidor maneja esto
    },
    shipping: {
      new: ['preparing'],
      pending: ['preparing'],
      preparing: ['ready', 'waiting_guide'],
      ready: ['waiting_guide'],
      waiting_guide: ['guide_generated'],
      guide_generated: ['shipped']
    }
  };
  
  return statusMap[deliveryType]?.[currentStatus] || [];
};

const OnlineOrdersView = ({ session, orderCount = 0, onOrderCountChange }) => {
  const [orders, setOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const wsRef = useRef(null);

  // Use parent's orderCount as the source of truth for new orders count
  const newCount = orderCount;

  // Initialize WebSocket connection
  useEffect(() => {
    if (!session?.branch?.id) return;

    const wsUrl = `${getWsUrl()}/api/v1/orders/ws/${session.branch.id}`;

    const connectWebSocket = () => {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected for orders');
      };

      wsRef.current.onmessage = (event) => {
        // Handle pong response (not JSON)
        if (event.data === 'pong') {
          return;
        }

        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_order') {
            // Add new order to the list
            setOrders(prev => [data.order, ...prev]);
            // Increment parent's counter
            onOrderCountChange?.(prev => prev + 1);
            // Sound is handled by POSMain.jsx
            toast.success(`¡Nuevo pedido! ${data.order.order_number}`);
          } else if (data.type === 'order_updated') {
            // Update existing order
            setOrders(prev => prev.map(o => o.id === data.order.id ? data.order : o));
          }
        } catch (e) {
          console.log('Non-JSON message received:', event.data);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected, reconnecting...');
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    connectWebSocket();

    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      wsRef.current?.close();
    };
  }, [session?.branch?.id]);

  // Load initial orders
  useEffect(() => {
    if (session?.branch?.id) {
      loadOrders();
      loadNewCount();
    }
  }, [session?.branch?.id]);

  // Note: Sound notifications are handled by POSMain.jsx to avoid duplication

  const loadOrders = async () => {
    try {
      const response = await getOnlineOrders(session.branch.id, false, session);
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNewCount = async () => {
    try {
      const response = await getOnlineOrdersCount(session.branch.id, session);
      // Sync parent's counter with backend value
      onOrderCountChange?.(response.data.count);
    } catch (error) {
      console.error('Error loading count:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await getOrderHistory(session.branch.id, 50, session);
      setHistoryOrders(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleSelectOrder = async (order) => {
    setSelectedOrder(order);

    // Mark as read if new
    if (order.status === 'new') {
      try {
        await markOrderAsRead(order.id, session);
        setOrders(prev => prev.map(o =>
          o.id === order.id ? { ...o, status: 'pending' } : o
        ));
        // Decrement parent's counter
        onOrderCountChange?.(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking order as read:', error);
      }
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedOrder) return;
    
    try {
      const response = await updateOrderStatus(
        selectedOrder.id,
        newStatus,
        newStatus === 'guide_generated' ? trackingNumber : null,
        session
      );
      
      // Update local state
      const updatedOrder = response.data;
      
      // If order is now completed, remove from active orders
      if (['delivered', 'shipped', 'cancelled'].includes(newStatus)) {
        setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
        setSelectedOrder(null);
        toast.success('Pedido completado');
      } else {
        setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
        setSelectedOrder(updatedOrder);
        toast.success(`Estado actualizado: ${STATUS_CONFIG[newStatus]?.label}`);
      }
      
      setTrackingNumber('');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar estado');
    }
  };

  const printOrderTicket = () => {
    if (!selectedOrder) return;
    
    const order = selectedOrder;
    const deliveryConfig = DELIVERY_TYPE_CONFIG[order.delivery_type] || {};
    
    const itemsHTML = (order.items || []).map(item => `
      <tr>
        <td style="text-align:left">${item.product_name}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:right">$${item.unit_price?.toFixed(2) || '0.00'}</td>
        <td style="text-align:right">$${item.subtotal?.toFixed(2) || '0.00'}</td>
      </tr>
    `).join('');
    
    const addressHTML = order.delivery_address ? `
      <div style="margin-top:15px; padding:10px; border:1px dashed #000;">
        <strong>📍 DIRECCIÓN DE ENTREGA:</strong><br/>
        ${order.delivery_address.street}<br/>
        ${order.delivery_address.colony}<br/>
        ${order.delivery_address.city}, ${order.delivery_address.state} ${order.delivery_address.zip_code}<br/>
        ${order.delivery_address.references ? `<em>Ref: ${order.delivery_address.references}</em>` : ''}
      </div>
    ` : '';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pedido ${order.order_number}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 10px; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
          .header h2 { margin: 0; font-size: 18px; }
          .badge { display: inline-block; padding: 4px 8px; background: #000; color: #fff; border-radius: 4px; margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { padding: 4px 0; font-size: 11px; }
          th { border-bottom: 1px solid #000; text-align: left; }
          .totals { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; }
          .totals div { display: flex; justify-content: space-between; margin: 3px 0; }
          .total-final { font-size: 16px; font-weight: bold; }
          .customer { background: #f0f0f0; padding: 10px; margin: 10px 0; }
          .notes { background: #fff3cd; padding: 10px; margin: 10px 0; border-left: 3px solid #ffc107; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>🛒 PEDIDO ONLINE</h2>
          <p style="font-size:14px; margin:5px 0;"><strong>${order.order_number}</strong></p>
          <div class="badge">${deliveryConfig.label || order.delivery_type}</div>
        </div>
        
        <div style="margin-bottom:10px">
          <div><strong>Fecha:</strong> ${new Date(order.created_at).toLocaleString('es-MX')}</div>
        </div>
        
        <div class="customer">
          <strong>👤 CLIENTE:</strong><br/>
          ${order.customer_name}<br/>
          📞 ${order.customer_phone}<br/>
          ${order.customer_email ? `✉️ ${order.customer_email}` : ''}
        </div>
        
        ${order.customer_notes ? `
          <div class="notes">
            <strong>📝 NOTAS:</strong><br/>
            ${order.customer_notes}
          </div>
        ` : ''}
        
        ${addressHTML}
        
        <table>
          <thead>
            <tr>
              <th style="text-align:left">Producto</th>
              <th style="text-align:center">Cant</th>
              <th style="text-align:right">P.Unit</th>
              <th style="text-align:right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
        
        <div class="totals">
          <div><span>Subtotal:</span><span>$${order.subtotal?.toFixed(2) || '0.00'}</span></div>
          <div><span>IVA:</span><span>$${order.tax?.toFixed(2) || '0.00'}</span></div>
          ${order.delivery_fee > 0 ? `<div><span>Envío:</span><span>$${order.delivery_fee.toFixed(2)}</span></div>` : ''}
          <div class="total-final"><span>TOTAL:</span><span>$${order.total?.toFixed(2) || '0.00'}</span></div>
        </div>
        
        <div style="text-align:center; margin-top:15px; border-top:1px dashed #000; padding-top:10px;">
          <p style="font-size:10px;">Pago: ${order.payment_method === 'card' ? 'Tarjeta' : order.payment_method === 'cash' ? 'Efectivo' : 'Transferencia'} - ${order.payment_status === 'paid' ? '✅ PAGADO' : '⏳ Pendiente'}</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const displayOrders = showHistory ? historyOrders : orders;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0">
      {/* Orders List - fullscreen on mobile, sidebar on desktop */}
      <div className={`${selectedOrder ? 'hidden md:flex' : 'flex'} w-full md:w-96 border-r border-slate-800 flex-col`}>
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-white">Pedidos Online</h2>
            {newCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                {newCount} nuevo{newCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowHistory(false); loadOrders(); }}
              className={`flex-1 py-2 text-sm rounded-lg ${!showHistory ? 'bg-teal-500 text-white' : 'bg-slate-800 text-slate-400'}`}
            >
              Pendientes
            </button>
            <button
              onClick={() => { setShowHistory(true); loadHistory(); }}
              className={`flex-1 py-2 text-sm rounded-lg ${showHistory ? 'bg-teal-500 text-white' : 'bg-slate-800 text-slate-400'}`}
            >
              Historial
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {displayOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 p-4">
              <ShoppingBag className="w-12 h-12 mb-2 opacity-50" />
              <p>{showHistory ? 'No hay pedidos en historial' : 'No hay pedidos pendientes'}</p>
            </div>
          ) : (
            displayOrders.map(order => {
              const deliveryConfig = DELIVERY_TYPE_CONFIG[order.delivery_type] || {};
              const DeliveryIcon = deliveryConfig.icon || Package;
              const statusConfig = STATUS_CONFIG[order.status] || {};
              
              return (
                <button
                  key={order.id}
                  onClick={() => handleSelectOrder(order)}
                  className={`w-full p-4 border-b border-slate-800 text-left hover:bg-slate-800/50 transition-colors ${
                    selectedOrder?.id === order.id ? 'bg-slate-800' : ''
                  } ${order.status === 'new' ? 'bg-red-900/20' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-white flex items-center gap-2">
                        {order.order_number}
                        {order.status === 'new' && (
                          <Bell className="w-4 h-4 text-red-400 animate-bounce" />
                        )}
                      </p>
                      <p className="text-sm text-slate-400">{order.customer_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <DeliveryIcon className={`w-4 h-4 text-${deliveryConfig.color}-400`} />
                        <span className="text-xs text-slate-500">{deliveryConfig.label}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-teal-400">${order.total?.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bg}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {new Date(order.created_at).toLocaleString('es-MX')}
                  </p>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Order Detail */}
      <div className={`${selectedOrder ? 'flex' : 'hidden md:flex'} flex-1 flex-col p-3 md:p-6 overflow-y-auto`}>
        {selectedOrder ? (
          <div className="max-w-2xl mx-auto space-y-4 md:space-y-6 w-full">
            {/* Header */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                {/* Mobile back button */}
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="md:hidden shrink-0 p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                  <h2 className="text-xl md:text-2xl font-bold text-white truncate">{selectedOrder.order_number}</h2>
                  <p className="text-sm md:text-base text-slate-400">{new Date(selectedOrder.created_at).toLocaleString('es-MX')}</p>
                </div>
              </div>
              <button
                onClick={printOrderTicket}
                className="shrink-0 flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm md:text-base"
              >
                <Printer className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Imprimir</span>
              </button>
            </div>

            {/* Delivery Type & Status */}
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const config = DELIVERY_TYPE_CONFIG[selectedOrder.delivery_type] || {};
                    const Icon = config.icon || Package;
                    return (
                      <>
                        <div className={`w-12 h-12 rounded-full bg-${config.color}-900/30 flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 text-${config.color}-400`} />
                        </div>
                        <div>
                          <p className="font-bold text-white">{config.label}</p>
                          <span className={`text-sm ${STATUS_CONFIG[selectedOrder.status]?.bg} px-2 py-0.5 rounded-full`}>
                            {STATUS_CONFIG[selectedOrder.status]?.label}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <p className="text-2xl font-bold text-teal-400">${selectedOrder.total?.toFixed(2)}</p>
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 space-y-3">
              <h3 className="font-bold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-teal-400" />
                Cliente
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Nombre</p>
                  <p className="text-white">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Teléfono</p>
                  <p className="text-white flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {selectedOrder.customer_phone}
                  </p>
                </div>
              </div>
            </div>

            {/* Delivery Address (if applicable) */}
            {(selectedOrder.delivery_address || selectedOrder.delivery_type === 'delivery') && (
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 space-y-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-400" />
                  Dirección de Entrega
                </h3>
                <div className="text-sm text-slate-300 space-y-2">
                  {/* Show address - handle both object and string formats */}
                  {typeof selectedOrder.delivery_address === 'object' ? (
                    <>
                      {selectedOrder.delivery_address.full_address ? (
                        <p className="text-white">{selectedOrder.delivery_address.full_address}</p>
                      ) : (
                        <>
                          {selectedOrder.delivery_address.street && <p>{selectedOrder.delivery_address.street}</p>}
                          {selectedOrder.delivery_address.colony && <p>{selectedOrder.delivery_address.colony}</p>}
                          {(selectedOrder.delivery_address.city || selectedOrder.delivery_address.state) && (
                            <p>{selectedOrder.delivery_address.city}{selectedOrder.delivery_address.city && selectedOrder.delivery_address.state && ', '}{selectedOrder.delivery_address.state} {selectedOrder.delivery_address.zip_code}</p>
                          )}
                        </>
                      )}
                      {selectedOrder.delivery_address.references && (
                        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-2 mt-2">
                          <p className="text-yellow-400 text-xs font-medium">📍 Referencias:</p>
                          <p className="text-slate-300">{selectedOrder.delivery_address.references}</p>
                        </div>
                      )}
                    </>
                  ) : selectedOrder.delivery_address ? (
                    <p className="text-white">{selectedOrder.delivery_address}</p>
                  ) : (
                    <p className="text-slate-500 italic">Sin dirección especificada</p>
                  )}
                  
                  {/* Show coordinates if available */}
                  {selectedOrder.delivery_coords && (
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-2 mt-2">
                      <p className="text-blue-400 text-xs font-medium">🗺️ Coordenadas GPS:</p>
                      <p className="text-slate-300 font-mono text-sm">
                        {selectedOrder.delivery_coords.lat?.toFixed(6)}, {selectedOrder.delivery_coords.lng?.toFixed(6)}
                      </p>
                      <a 
                        href={`https://www.google.com/maps?q=${selectedOrder.delivery_coords.lat},${selectedOrder.delivery_coords.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-xs underline mt-1 inline-block"
                      >
                        Abrir en Google Maps →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customer Notes / Additional Notes */}
            {(selectedOrder.customer_notes || selectedOrder.notes || selectedOrder.customer_info?.notes) && (
              <div className="bg-yellow-900/20 rounded-xl p-4 border border-yellow-500/30">
                <h3 className="font-bold text-yellow-400 flex items-center gap-2 mb-2">
                  📝 Notas Adicionales
                </h3>
                <p className="text-white text-sm">
                  {selectedOrder.customer_notes || selectedOrder.notes || selectedOrder.customer_info?.notes}
                </p>
              </div>
            )}

            {/* Tracking Number (for shipping) */}
            {selectedOrder.delivery_type === 'shipping' && selectedOrder.tracking_number && (
              <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-500/30">
                <p className="text-purple-400 text-sm font-medium">📦 Número de Guía:</p>
                <p className="text-white text-lg font-mono mt-1">{selectedOrder.tracking_number}</p>
              </div>
            )}

            {/* Order Items */}
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <h3 className="font-bold text-white mb-4">Productos</h3>
              <div className="space-y-3">
                {(selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                    <div>
                      <p className="text-white">{item.product_name}</p>
                      <p className="text-sm text-slate-500">${item.unit_price?.toFixed(2)} x {item.quantity}</p>
                    </div>
                    <p className="font-bold text-teal-400">${item.subtotal?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-700 mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>IVA</span>
                  <span>${selectedOrder.tax?.toFixed(2)}</span>
                </div>
                {selectedOrder.delivery_fee > 0 && (
                  <div className="flex justify-between text-slate-400">
                    <span>Envío</span>
                    <span>${selectedOrder.delivery_fee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold text-white">
                  <span>Total</span>
                  <span className="text-teal-400">${selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            {!['delivered', 'shipped', 'cancelled'].includes(selectedOrder.status) && (
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 space-y-4">
                <h3 className="font-bold text-white">Cambiar Estado</h3>
                
                {/* Tracking number input for shipping */}
                {selectedOrder.delivery_type === 'shipping' && selectedOrder.status === 'waiting_guide' && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Número de Guía</label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Ingresa el número de guía"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    />
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {getNextStatuses(selectedOrder.delivery_type, selectedOrder.status).map(status => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(status)}
                      disabled={status === 'guide_generated' && !trackingNumber}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                      {STATUS_CONFIG[status]?.label}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handleUpdateStatus('cancelled')}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="hidden md:flex flex-col items-center justify-center h-full text-slate-500">
            <ShoppingBag className="w-16 h-16 mb-4 opacity-50" />
            <p>Selecciona un pedido para ver los detalles</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineOrdersView;
